import type { CompanyVideo } from "@/modules/companies/client/api/types"
import type { FunctionComponent } from "react"

import { clsx } from "clsx"
import dynamic from "next/dynamic"
import Image from "next/image"
import { useCallback, useState } from "react"

import { Text } from "@jobteaser/spark/components/Text"

import { MediaImage } from "@/modules/companies/client/components/Media/Image"
import { getPlayIconProps } from "@/modules/companies/client/components/Media/utils/getPlayIconProps"
import { useTranslation } from "@/modules/i18n/components/useTranslation"
import { useLocale } from "@/modules/locales/useLocale"
import { useTracking } from "@/modules/tracking/useTracking"

import styles from "./Media.module.css"

const VideoPlayerDynamic = dynamic(
  () => import("@jobteaser/ui-kit/components/VideoPlayer/VideoPlayer").then(mod => mod.VideoPlayer),
  { ssr: false }
)

export type MediaVideoTracking = {
  name?: string
  id?: string
}

export type MediaVideoProps = {
  className?: string
  onStart?: () => void
  priority?: boolean
  video: CompanyVideo
  withDescription?: boolean
  tracking?: MediaVideoTracking
}

export const MediaVideo: FunctionComponent<MediaVideoProps> = ({
  className,
  onStart,
  priority,
  video,
  withDescription = true,
  tracking: trackingData,
}) => {
  const locale = useLocale()
  const tracking = useTracking()
  const { t } = useTranslation(["companies_client"])

  const { description, previewUrl, url } = video

  const isVideoWithThumbnail = !!previewUrl
  const [isThumbnailDisplayed, setIsThumbnailDisplayed] = useState(isVideoWithThumbnail)

  const onVideoStart = useCallback(() => {
    tracking?.track("media_video_click", {
      company_id: trackingData?.id || "",
      company_name: trackingData?.name || "",
      service: "company",
      trackee_locale: locale,
      video_id: url,
    })
    return onStart?.()
  }, [tracking, onStart, trackingData, url, locale])

  return (
    <div className={clsx(styles.videoContainer, className)}>
      <div
        className={styles.thumbnail}
        onClick={() => setIsThumbnailDisplayed(false)}
        onKeyDown={() => setIsThumbnailDisplayed(false)}
        role="button"
        tabIndex={0}
      >
        {isThumbnailDisplayed && (
          <>
            <Image
              alt={t("companies_client.media.video.iconPlayerAlt")}
              {...getPlayIconProps(video.url)}
              height={48}
              width={68}
              priority={priority}
            />
            <MediaImage
              wrapperClassName={styles.thumbnailImageWrapper}
              className={styles.thumbnailImage}
              src={previewUrl}
              priority={priority}
              alt={t("companies_client.media.video.thumbnailAlt")}
              aria-labelledby={description}
            />
          </>
        )}
        {!isThumbnailDisplayed && (
          <div data-testid="company_information_video">
            <VideoPlayerDynamic playing={isVideoWithThumbnail} url={url} onStart={onVideoStart} />
          </div>
        )}
      </div>
      {withDescription && description && (
        <div className={styles.note} id={description}>
          <Text variant="body1">{description}</Text>
        </div>
      )}
    </div>
  )
}
